/**
 * Every explanatory string in this prototype, in one file.
 *
 * Copy lives here rather than beside the components that render it so it can be
 * read and edited as copy: open this file next to the running prototype and a
 * save shows up in place. Components import from here and declare no strings of
 * their own, so a term can't end up defined two ways in two files without that
 * being visible right here.
 *
 * Two conventions appear inside the strings themselves:
 *
 *   **bold**   emphasises a span
 *   {name}     interpolates a figure the component supplies
 *
 * Both are rendered by `renderCopy`, or by `fillCopy` where the result has to be
 * a plain string rather than markup. Most strings need neither and are rendered
 * directly; only the modal, the lift tags, and the metric footers use them.
 *
 * The voice is uniform across all of these, which is what keeps a hundred-odd
 * tooltips reading as one product rather than a hundred authors:
 *
 *   - Definitions are noun phrases, not sentences about the reader: "Revenue
 *     from all orders placed within 14 days of a click", not "This shows you
 *     the revenue from...". A second sentence carries the caveat or the
 *     arithmetic.
 *   - Faire is named where a sentence needs an actor: "Faire attributes a
 *     conversion to an ad click". "We" is never used.
 *   - "you" and "your" belong to the brand reading the page: your ads, your
 *     budget, retailers that have never ordered from you.
 *   - Customers are acquired or reorder *through* ads, never *via* them.
 *   - Forecasts are impersonal: "Estimated total revenue over 12 months",
 *     "are expected to bring in". Never "we expect".
 *   - Ad sales, First-year reorders, First-year sales (actual), and First-year
 *     sales (forecasted) keep their capital when a string points at the column
 *     by name.
 *
 * Apostrophes are ASCII throughout.
 *
 * Not yet centralized: table column labels and filter option labels.
 */
export const COPY = {
  /** Strings used by more than one tab, so a term can't drift between them. */
  shared: {
    /**
     * The gray cells in the forward-looking columns on a reorder-ad row: the
     * cohort table's First-year reorders, and Lifetime sales and Lifetime orders
     * in both the cohort and Customers tables. One reason for all of them,
     * because they fail for one reason: the ad bought a single order and can't
     * claim what surrounds it.
     */
    reorderNotApplicable:
      "Doesn't apply to reorder ads. A reorder ad's credit stops at the reorder itself, so there's no continued or lifetime figure to attribute to it.",

    /**
     * The four per-retailer columns the Customers table and both versions of
     * the cohort table put the same headers on. They sit here rather than in
     * either tab's block because a reader hovering Lifetime sales on the cohort
     * drilldown is asking exactly what a reader hovering it on the Customers
     * tab is, and a definition kept in three places is a definition that
     * drifts.
     */
    columns: {
      /**
       * The cell shows a store type and says nothing about where it came from,
       * and the two sources aren't equally firm: the retailer declared it, or
       * Faire inferred it. Naming the fallback openly costs a long second
       * sentence and buys the thing a brand needs before it segments spend by
       * store type, which is knowing which rows rest on a guess.
       */
      storeType:
        'Retailer-selected store type. For retailers that haven\'t selected a store type or chose "Other," Faire predicts the closest match based on information about their business and how similar stores describe themselves to help you better understand your customers.',
      /**
       * Which order carries the date depends on the ad type, and that is the
       * one thing about the column the cells can't show. Leading on the
       * conversion rather than on either order hangs both readings off a single
       * noun phrase, so neither ad type reads as the special case.
       */
      orderDate:
        "Date of conversion: for a new customer ad, the order that converted the retailer; for a reorder ad, the reorder itself.",
      /**
       * Paired with `lifetimeSales` and deliberately the same sentence with one
       * noun swapped: the two columns sit side by side over the same history
       * and differ only in what they count, so reading differently would imply
       * they measure different windows.
       *
       * The caveat names new customer ads rather than promoted listings because
       * that is the line the column actually draws: a reorder ad is a promoted
       * listing too, so the wider term would claim a figure for every row while
       * half of them come back gray. Naming the narrower one keeps the header
       * and `reorderNotApplicable` telling one story — which ads produce a
       * lifetime figure at all, and why this row hasn't got one.
       */
      lifetimeOrders:
        "Total orders from this customer. Lifetime orders are only tracked for customers acquired through new customer ads.",
      /** Same sentence as `lifetimeOrders`, for the reason given there. */
      lifetimeSales:
        "Total revenue received from this customer. Lifetime sales are only tracked for customers acquired through new customer ads.",
    },

    /**
     * The forecast explainer, reachable from every figure that is partly
     * projected: the cohort view's first-year metric, and the Overview's
     * first-year sales and first-year ROAS columns and legend.
     *
     * Shared rather than owned by one of them because those figures move on
     * their own, and a reader who asks why is asking the same question
     * wherever they happen to be standing. One answer, one place to fix it.
     */
    forecast: {
      /**
       * Also the modal's trigger everywhere, so link and dialog stay paired.
       * The dialog title carries the possessive and this one deliberately
       * doesn't: a trigger reads tighter without it, a title warmer with it.
       */
      link: "How we forecast first-year sales",
      /**
       * The same trigger where it sits inside a visible sentence rather than at
       * the end of a tooltip. The sentence in front of it has already said what
       * is being forecast, so repeating "first-year sales" in the link would
       * name it twice in one breath. Opens the same dialog.
       */
      inlineLink: "How we forecast",
      modal: {
        title: "How we forecast your first-year sales",
        confirm: "Got it",
        /**
         * A blank line starts a new paragraph. Rewrite it as prose; add or
         * remove paragraphs freely.
         *
         * Ordered as the reader's questions arrive: what is being forecast,
         * where the starting number comes from, why it keeps moving, and only
         * then the reassurance that moving is the point. The closing paragraph
         * is labelled because that last question is the one people arrive with
         * and it should be findable without reading the three above it.
         */
        body: `
When you acquire a customer through a new customer ad, we forecast how much they're likely to order from you in their first 12 months.

We base this on your own history: we look at the customers you've already acquired who've been with you for at least a year, and take their average first-year order volume. That gives us a realistic starting point for what a new customer is worth to you.

From there, the forecast updates every month. As a new customer places real orders, we fold those actuals into the estimate and re-forecast the rest of their first year. So the number sharpens over time — starting as an informed estimate and becoming more grounded in reality each month, until it's a real, actual figure at the 12-month mark.

**Why it changes:** More data means a better forecast. Rather than locking in a day-one guess, we keep the number honest by updating it as we learn how your customers actually behave.
`,
      },
    },
  },

  overview: {
    heading: "Performance overview",
    /**
     * Just the frame. The refresh cadence and the promotions caveat used to sit
     * here too, but they describe the month table's figures specifically, so
     * they moved down to it — a reader hitting them at the top of the page has
     * no columns in front of them yet.
     */
    subhead: "An overview of your Promoted Listings performance.",
    /**
     * Anchor of the three tab subheads V3 writes as a set: one clause each,
     * opening on "See", naming Promoted Listings once. The word "overview" is
     * out of all three — one of the tabs is called Overview, so a subhead that
     * opens with it reads as a label for the tab rather than a description of
     * the page. `customers.v3Subhead` and `products.v3Subhead` are the others.
     */
    v3Subhead: "See how your Promoted Listings are performing.",

    kpi: {
      /**
       * Card labels. Three of the four say "Total" because they roll the
       * selected range up into one figure. First-year ROAS is a ratio, so
       * "Total" would claim something it isn't.
       */
      totalAdSalesTitle: "Total ad sales",
      totalOrderMonthSalesTitle: "Total ad sales",
      adSpendTitle: "Total ad spend",
      conversionsTitle: "Total conversions",
      firstYearRoasTitle: "First-year ROAS",
      totalSalesOverSpendTitle: "Total ad sales / spend",

      /**
       * The headline figure is a sum of two ad types with different attribution
       * windows, and that is the one thing the number itself cannot show: a
       * dollar of new customer ad sales can arrive up to a year after the click,
       * while a dollar of reorder ad sales is one order and done. Naming both
       * windows in the parentheses is what stops a reader reconciling this card
       * against the two footers beneath it and concluding the arithmetic is off.
       *
       * The rest of each definition belongs to the ad types dialog, so the
       * tooltip ends in the link rather than growing a third sentence. Rendered
       * through `adTypesTooltip`, which requires `interactive` on HeaderTooltip.
       */
      totalAdSales:
        "Total revenue from all orders attributed to your promoted listings. This includes new customer ad sales (every order a new customer places within 12 months of their first) plus reorder ad sales (the single order placed by an existing customer).",
      totalOrderMonthSales:
        "Total ad sales recorded in the selected order months, including orders from that month's ad clicks and reorders from customers acquired through ads in the previous 12 months.",
      /**
       * V3's Total ad sales card. Same sentence as `monthTable.v3TotalSales`,
       * because the card and the column are the same roll-up. Edit them together.
       */
      v3TotalOrderMonthSales:
        "Revenue from all ad-attributed orders, including orders from in-month ad clicks (immediate sales) and reorders from past ad-acquired customer (continued sales).",
      adSpend: "Total amount spent on promoted listings.",
      /**
       * Named the product rather than the ad types, unlike
       * `customers.kpi.conversions.all` one tab over, which still defines the
       * same metric by naming the two ads that produce it. They no longer match
       * word for word, so edit them independently.
       */
      conversions:
        "Total new customer acquisitions and reorder conversions from promoted listings.",
      firstYearRoas:
        "Overall first-year return on Faire advertising. Calculated by dividing first-year sales (forecasted) from all advertising months by total ad spend.",
      totalSalesOverSpend:
        "Total sales recorded in the selected months divided by ad spend in those same months.",
      v3TotalSalesOverSpend:
        "Ratio of the month's ad-attributed revenue to ad spend.",

      /**
       * The split beneath each figure, shown only under All ad types. `{value}`
       * is the figure the card supplies; these are filled by `fillCopy` rather
       * than `renderCopy`, since a footer is a plain string.
       *
       * First-year sales is two addends: new-customer ads (ad sales
       * plus first-year reorders from those customers) and reorder ads. Spend
       * names the ad type that consumed the money. Conversions use the same
       * "from" split as sales, so the two cards name the same two sources.
       */
      footers: {
        adSalesNew: "{value} from new customer ads",
        adSalesReorder: "{value} from reorder ads",
        adSpendNew: "{value} on new customer ads",
        adSpendReorder: "{value} on reorder ads",
        conversionsNew: "{value} from new customer ads",
        conversionsReorder: "{value} from reorder ads",
        firstYearRoasNew: "{value} forecasted first-year ROAS on new customers",
        firstYearRoasReorder: "{value} ROAS on reorder conversions",
        totalSalesOverSpendNew: "{value} for new customers",
        totalSalesOverSpendReorder: "{value} for reorder conversions",
      },
    },

    monthTable: {
      title: "Ad performance by conversion month",
      adClickMonthTitle: "Performance by ad-click month",
      adClickMonthDescription:
        "How your budget was spent, and all ad-attributed sales organized by the month of the ad click. Each month's total sales include immediate ad sales from {adTypes}, as well as the reorders placed by the customers you acquired that month.",
      orderMonthTitle: "Performance by order month",
      orderMonthDescription:
        "How your budget was spent, and all ad-attributed sales organized by the month the order was placed. Each month's total ad sales include immediate ad sales from {adTypes}, as well as the reorders placed by any customer acquired in the 12 months prior to the order month.",
      /**
       * V2 drops "order month" from the title and the first sentence: immediate
       * sales follow the ad click, so an order can land in the next calendar
       * month and still sit on this row. Reorders in the third section did
       * happen in the named month, which the second sentence still says.
       */
      v2Title: "Performance by month",
      v2Description:
        "How your budget was spent and total ad sales per month. Each month's total ad sales include immediate ad sales from {adTypes}, as well as the reorders placed that month by any customer acquired in the previous 12 months.",
      /**
       * V3's title tooltip. No `{adTypes}` slot: the previous sentence dropped
       * the link and left a hole. Names spend and total ad sales, then the two
       * addends of that total.
       */
      v3Description:
        "Monthly summary of your Promoted Listings performance. Shows your ad spend and your total ad sales — sales from in-month ad conversions plus reorders from customers you previously acquired through ads.",
      adTypesLink: "new customer ads and reorder ads",
      /** Groups the click-month return columns vs the first-year pair. */
      groupImmediate: "Immediate performance",
      groupFirstYear: "First-year performance",
      groupSpend: "Ad spend",
      groupClickOrders: "Orders from in-month ad clicks",
      groupClickContinued: "Reorders from each month's ad-acquired customers",
      groupOrderContinued:
        "Reorders from previous months' ad-acquired customers",
      groupAllAdAttributedOrders: "All ad-attributed orders",
      /**
       * Everything about this table a reader can't get from the column headers:
       * which month the rows are grouped by, when each column refreshes, and
       * that sales are net of promotions. The refresh cadence and the promotions
       * caveat used to sit in a paragraph under the title; they were the same
       * two sentences, so they moved in here rather than being restated beside
       * it. This is now the only place the click-month rule is stated, so it
       * can't be dropped to save space.
       */
      titleTooltip:
        "Rows are grouped by the month of the ad click, not the month the order arrived. Impressions, clicks, conversions, and ad sales are updated daily, while ad spend and utilization are updated weekly. All sales figures reflect your revenue after customer promotions have been applied.",

      /** Shared word-for-word with the sales-and-spend charts' Ad spend legend item. */
      adSpend: "Total amount spent on promoted listings each month.",
      conversions:
        "New customer acquisitions and reorder conversions from promoted listings each month.",
      firstYearAdSales:
        "How much we estimate you will have made in 12 months' time, given the reorders from customers acquired in each month.",
      /**
       * The dashed first-year sales pill. Same idea as `firstYearAdSales`,
       * named "this month" because it sits on one row rather than the column.
       */
      firstYearAdSalesProjected:
        "How much we estimate you will have made in 12 months' time, given the reorders from customers acquired this month.",
      /** Shared word-for-word with the ROAS chart's Immediate legend item. */
      firstOrderRoas:
        "Immediate return on Faire advertising. Calculated by dividing each month's ad sales by ad spend.",
      /**
       * V3's Immediate ROAS column and the matching chart legend. Names the
       * numerator Immediate sales rather than "ad sales", which V3 no longer
       * uses as a column.
       */
      v3FirstOrderRoas:
        "Immediate return on ad spend. Calculated by dividing the month's immediate sales by ad spend.",
      /**
       * Word-for-word `overview.charts.roas.firstYear`, the same way
       * `firstOrderRoas` matches that chart's Immediate legend item. The legend
       * and the column carry the same label, so they get the same definition —
       * a reader who hovers both shouldn't have to work out whether two
       * different sentences describe two different metrics. Move them together.
       */
      firstYearRoas:
        "The return we estimate you will have in 12 months' time, given the reorders from customers acquired in each month.",
      /**
       * The dashed first-year ROAS pill. Same idea as `firstYearRoas`, named
       * "this month" because it sits on one row rather than the column.
       */
      firstYearRoasProjected:
        "The return we estimate you will have in 12 months' time, given the reorders from customers acquired this month.",

      /**
       * The reorder-ads readings of the two first-year columns. Both columns now
       * report a figure under that filter rather than going blank: the metric is
       * counted for reorder ads under All ad types too, it just has nothing
       * beyond the immediate sale in it. So these say what the figure equals
       * instead of saying the column doesn't apply, and they carry no forecast
       * link — there is nothing left to forecast.
       */
      firstYearAdSalesReorder:
        "The same as Ad sales for reorder ads. A reorder ad's sale lands in the month of the ad click and nothing continues past it, so the first year is already complete.",
      firstYearRoasReorder:
        "The same as ROAS for reorder ads. With no first-year reorders to accumulate, the immediate return is already the final one.",

      /** All ad types shows one budget figure; a single ad type shows its share of it. */
      utilization:
        "Percentage of the month's budget that is expected to be spent, or was spent, on ads.",
      /**
       * The dashed pill on the in-progress month. The column tooltip covers
       * spent-or-expected in one sentence; this one is only the estimate, so
       * it can say how the estimate is made.
       */
      utilizationProjected:
        "An estimate of how much of your budget will be spent, based on retailer activity so far.",
      /**
       * The % of budget column, which replaces Utilization once the filter picks
       * one ad type. Named rather than "this ad type's", since the column only
       * ever appears under a filter and the reader shouldn't have to look back
       * up at the chip to know which spend the percentage is of.
       *
       * No `all` reading: under all ad types the column is Utilization instead,
       * so a share of the budget by ad type has nothing to be a share of.
       */
      budgetShare: {
        new: "The amount spent on new customer ads as a percentage of the month's budget.",
        returning:
          "The amount spent on reorder ads as a percentage of the month's budget.",
      },

      /**
       * One sentence for every ad type. The column means the same thing under
       * each of them — revenue from the order the click drove — so narrowing the
       * filter narrows the rows, not the definition.
       *
       * Shared word-for-word with the sales-and-spend charts' Ad sales
       * legend item.
       *
       * The month goes in front of "click" rather than at the end of the
       * sentence, matching `cohort.kpi.immediateAdSales`. Trailing it reads as
       * though the month bounded the order, which is the opposite of the rule
       * the column follows: the click sets the month, and the order it drove
       * counts there even when it arrives in the next one.
       */
      immediateAdSales:
        "Revenue from all orders placed within 14 days of each month's new customer ad click or reorder ad click.",
      /**
       * V3's Immediate sales column and the matching chart legend. Drops
       * "each month's" so the definition is about the click, not the row.
       */
      v3ImmediateAdSales:
        "Revenue from orders placed by a retailer within 14 days of clicking a new customer ad or reorder ad.",

      /**
       * First-year sales (actual) in the month table. Names both addends so the
       * lift tag beside it still has a metric to point at, then states the
       * 12-month window on the reorder half: those dollars stop accruing after
       * the customer's first year.
       *
       * Word-for-word `cohort.v2.chart.salesToDate` with "each month" in place
       * of the named month, so this sentence and the Total ad sales series in
       * the drilldown a click away define the same figure. Edit them together.
       */
      firstYearSalesActual:
        "Ad sales plus first-year reorders: everything each month's ad conversions have brought in so far. Reorders from ad-acquired new customers count only for the first 12 months after their first order.",
      continuedSales:
        "Revenue from reorders placed within 12 months by customers acquired through new customer ads in each ad-click month.",
      orderMonthContinuedSales:
        "Revenue from reorders placed in each order month by customers acquired through new customer ads in the previous 12 months.",
      /**
       * V3's Continued sales column and the matching chart legend. States the
       * 12-month attribution window as a second sentence so it isn't buried
       * inside "previous 12 months."
       */
      v3ContinuedSales:
        "Revenue from reorders placed by customers acquired through a new customer ad. Reorders from these customers are attributed to ads for a year after their acquisition.",
      totalSales:
        "Immediate sales plus continued sales attributed to each month.",
      /**
       * V3's Total ad sales column, KPI card, and matching chart total.
       * Word-for-word `kpi.v3TotalOrderMonthSales`. Edit them together.
       */
      v3TotalSales:
        "Revenue from all ad-attributed orders, including orders from in-month ad clicks (immediate sales) and reorders from past ad-acquired customer (continued sales).",
      totalSalesOverSpend:
        "Immediate sales plus continued sales recorded in a month, divided by ad spend in that same month.",
      /**
       * V3's Total ad sales / spend column, KPI card, and matching chart
       * legend. Word-for-word `kpi.v3TotalSalesOverSpend`. Edit them together.
       */
      v3TotalSalesOverSpend:
        "Ratio of the month's ad-attributed revenue to ad spend.",

      /**
       * Em-dash cells under reorder ads. Unused on First-year sales (actual),
       * which reports the immediate figure instead; kept for the lift tag's
       * hover when a reader asks why that tag is missing.
       */
      disabledContinued:
        "Reorder ad sales land in the month of the ad click, so reorder ads have nothing that continues past it.",

      /**
       * The +% tag beside First-year sales (actual). It is how the table
       * explains first-year reorders once that figure is no longer its own
       * column: the dollars in the cell are the total, and the tag is the
       * reorders' share of Ad sales.
       *
       * The table is the only place this tag lives: the drilldown it opens
       * leads with the first-year pair and has no total to hang a lift off.
       * It opens on the month, then points back at it with "since then" and
       * "this month's" rather than naming it three times in one sentence.
       *
       * {count} and {orders} come from the cohort's additional-order count, with
       * singular/plural chosen by the caller, and the tag drops to a bare
       * percentage when there is no count to name.
       */
      liftTag:
        "The new customers acquired in {month} placed **{count}** more {orders} since then, adding **{pct}%** on top of this month's ad sales.",
      /**
       * Order-month Total sales lift: the extra is reorders placed that month by
       * customers acquired in the prior year, not that month's own cohort.
       */
      orderMonthLiftTag:
        "Customers acquired in the 12 months before {month} reordered that month, adding **{pct}%** on top of this month's immediate sales.",
    },

    /**
     * First-year reorders, keyed by the ad type filter. The month table no
     * longer has this as its own column — the lift tag on First-year sales
     * (actual) is what carries the definition now — but the sales-and-spend
     * charts' legend items still name the series.
     *
     * `all` is shared word-for-word with those legend items; the forecast cut
     * of that chart splits it and keeps "already" on its actual half. Edit them
     * together.
     */
    continuedAdSales: {
      all: "Revenue from all orders placed by each month's new customers within 12 months of their first order.",
      new: "Revenue from all orders placed by each month's new customers within 12 months of their first order.",
      returning:
        "Does not apply to reorder ads. Faire does not take credit for orders placed by a retailer after a reorder ad sale.",
    },

    adTypesModal: {
      /**
       * Also the accessible name of the info button on the ad type filter that
       * opens the dialog, so what a screen reader announces and what the dialog
       * is titled are the same sentence.
       */
      title: "What are new customer ads and reorder ads?",
      /**
       * The dialog's other trigger, inside the Total first-year sales tooltip. It sits
       * with the dialog it opens, the way `shared.forecast.link` does, so the
       * two can't drift into naming the subject differently.
       *
       * Not the title reused: a question works as a heading but reads as one
       * more thing to answer when it's the last line of a tooltip, where the
       * reader wants to be told there is more and where it is.
       */
      link: "Learn about the different ad types",
      confirm: "Got it",
      /**
       * A blank line starts a new paragraph. Rewrite it as prose; add or remove
       * paragraphs freely.
       *
       * The 14-day window applies to both ad types, so it's stated once up top
       * rather than repeated in each definition. Each term then sits at the
       * front of its own paragraph, since the terms are what the reader came
       * for.
       */
      body: `
Faire attributes a conversion to an ad click when the retailer orders within 14 days of the click. The sale counts toward the month of the click, even when the order itself arrives in the following month. Which ad gets the credit depends on whether they've ordered from you before.

**New customer ads** reach retailers that have never ordered from you. To reflect the long-term value of wholesale advertising, Faire counts every order an ad-acquired customer places within 12 months of their first order as an ad sale.

**Reorder ads** reach retailers that have ordered from you before. Here, Faire only counts that reorder as an ad sale.

The two ad types never compete for the same retailer. After a new customer's first order, they do not see a reorder ad for 12 months. And once a retailer orders from a reorder ad, they won't see another reorder ad for 14 days.
`,
    },

    charts: {
      /** Shared by the forecast-remainder cut; the actuals chart has its own title. */
      salesAndSpendTitle: "First-year sales (forecasted) and ad spend",

      /**
       * The hover panel's footer action, opening the drilldown for the month the
       * panel describes. One word: the panel's heading already names the month,
       * so "Explore January 2026" would say it twice, and the button has to fit
       * a panel sized by its figures.
       */
      explore: "Explore",

      /**
       * This chart's legend labels are the month table's column headers, so the
       * three tooltips below are word-for-word `monthTable.immediateAdSales`,
       * `continuedAdSales.all` and `monthTable.adSpend`. A reader who hovers a
       * legend swatch and then the column of the same name is asking one
       * question, and two wordings would read as two metrics. Edit them
       * together — the same pairing `firstOrderRoas` has with `roas.immediate`.
       */
      actuals: {
        title: "Total sales and ad spend by ad-click month",
        description:
          "The ad sales from each month's new customer and reorder ads, and the revenue from reorders placed by the new customers acquired each month in their first 12 months.",
        orderMonthTitle: "Total sales and spend by order month",
        orderMonthDescription:
          "The ad sales from each month's new customer and reorder ad clicks, and reorders placed that month by customers acquired through ads in the previous 12 months.",
        /**
         * V2's title: same series as `orderMonthTitle`, without naming an
         * accounting basis the immediate bars don't strictly follow.
         */
        v2Title: "Total sales and spend",
        v3Title: "Total ad sales and spend",
        /** Reorder ads have no cohort maturing, so the chart drops those series. */
        descriptionReorder:
          "Ad sales and ad spend from reorder ads in each month.",
        totalLabel: "First-year sales (actual)",
        orderMonthTotalLabel: "Total sales",
        v3OrderMonthTotalLabel: "Total ad sales",
        immediate:
          "Revenue from all orders placed within 14 days of each month's new customer ad click or reorder ad click.",
        /**
         * Word-for-word `monthTable.v3ImmediateAdSales`. Edit them together.
         */
        v3Immediate:
          "Revenue from orders placed by a retailer within 14 days of clicking a new customer ad or reorder ad.",
        continued:
          "Revenue from all orders placed by each month's new customers within 12 months of their first order.",
        orderMonthContinued:
          "Revenue from reorders placed this month by customers acquired through new customer ads in the previous 12 months.",
        /**
         * Word-for-word `monthTable.v3ContinuedSales`. Edit them together.
         */
        v3Continued:
          "Revenue from reorders placed by customers acquired through a new customer ad. Reorders from these customers are attributed to ads for a year after their acquisition.",
        spend: "Total amount spent on promoted listings each month.",
      },

      /**
       * The other cut of the same chart, so it carries the same table-column
       * tooltips as `actuals` above — see the note there. The one departure is
       * `continued`: this chart splits that column into an actual half and a
       * forecasted half, so its actual half keeps "already" to tell it apart
       * from the item directly below it. Otherwise it is the table's sentence.
       */
      forecast: {
        description:
          "The ad sales, the first-year reorders earned so far, the first-year reorders still forecasted before the 12-month mark, and ad spend in each month.",
        firstYearLabel: "First-year sales (forecasted)",
        /**
         * The label doesn't say "remainder": it sits directly beneath
         * "First-year reorders (actual)", and actual-plus-forecast already reads
         * as the two halves of the total.
         */
        remainderLabel: "First-year reorders (forecasted)",
        immediate:
          "Revenue from all orders placed within 14 days of each month's new customer ad click or reorder ad click.",
        continued:
          "Revenue from all orders each month's new customers have already placed within 12 months of their first order.",
        remainder:
          "Forecast of the first-year reorders still expected from these customers between now and 12 months after their first order.",
        spend: "Total amount spent on promoted listings each month.",
      },

      cumulative: {
        title: "First-year sales (forecasted) and ad spend, cumulative",
        description:
          "The combined ad sales, first-year reorders (both actual so far and forecasted), and ad spend for all of your customers, across every month and accumulated as each month's customers mature.",
        firstYearLabel: "First-year sales (forecasted)",
        continuedForecastLabel: "First-year reorders (forecasted)",
        immediate:
          "Cumulative revenue from all orders placed within 14 days of each month's new customer ad clicks or reorder ad clicks.",
        forecast:
          "Cumulative forecast of the first-year reorders expected within 12 months of each customer's first order.",
        actual:
          "Cumulative revenue from the orders these customers placed after the one that acquired them, within 12 months of that first order.",
        spend:
          "Total amount spent on ads to acquire these customers. It is flat across maturity because the spend all lands in the acquisition month.",
      },

      roas: {
        title: "Immediate and first-year ROAS",
        description:
          "The return on each month's ad spend, and the first-year return that month is forecasted to reach once every customer acquired that month has matured for 12 months.",
        descriptionReorder:
          "The return on each month's ad spend. Reorder ad sales land in the month of the ad click, so this return is final rather than a figure that keeps growing.",
        orderMonthTitle: "Immediate ROAS and total sales over spend",
        v3OrderMonthTitle: "Immediate ROAS and total ad sales over spend",
        orderMonthDescription:
          "ROAS from orders tied to ad clicks that month, compared with all ad-attributed sales recorded that month divided by that month's ad spend.",
        /**
         * Word-for-word `overview.monthTable.firstOrderRoas`. The chart legend
         * and the table column carry the same label, so they get the same
         * definition — a reader who hovers both shouldn't have to work out
         * whether two different sentences describe two different metrics. Move
         * them together.
         */
        immediate:
          "Immediate return on Faire advertising. Calculated by dividing each month's ad sales by ad spend.",
        /**
         * Word-for-word `monthTable.v3FirstOrderRoas`. Edit them together.
         */
        v3Immediate:
          "Immediate return on ad spend. Calculated by dividing the month's immediate sales by ad spend.",
        /**
         * Parallel to `immediate` above: same opening shape, same arithmetic
         * sentence, then the caveat that this one is an estimate until the
         * cohort is twelve months old.
         */
        firstYear:
          "The return we estimate you will have in 12 months' time, given the reorders from customers acquired in each month.",
        totalSalesOverSpend:
          "Ad sales and continued sales recorded in a month, divided by ad spend in that same month.",
        /**
         * Word-for-word `monthTable.v3TotalSalesOverSpend`. Edit them together.
         */
        v3TotalSalesOverSpend:
          "Ratio of the month's ad-attributed revenue to ad spend.",
        /**
         * Hung off the V3 overview ROAS chart's dotted reference. The brand's
         * ratio is not in the legend label — it appears when hovering the
         * line. The legend tooltip explains the ratio and offers a dead
         * "Update" link (no destination in this prototype).
         */
        breakeven:
          "Return on ad spend for you to break even. Calculated using the cost of goods you provided, and takes into account your Faire fees on Marketplace orders.",
        breakevenUpdate: "Update breakeven ROAS",
      },

      bySource: {
        title: "Ad sales by order month",
        description:
          "Revenue attributed to your ads, including new customer ads, reorder ads, and first-year reorders from the customers your new customer ads acquired in previous months.",
        newAds:
          "Revenue from first orders placed by customers acquired through new customer ads in the selected month.",
        reorderAds:
          "Revenue from orders placed through reorder ads in the selected month.",
        previousMonths:
          "First-year reorders from customers acquired in earlier months, attributed to the selected order month.",
        /** This chart's hover rows are sentences rather than series labels. */
        hoverNewAds: "{amount} in sales from new customer ads this month",
        hoverReorderAds: "{amount} in sales from reorder ads this month",
        hoverPreviousMonths:
          "{amount} in sales from customers acquired through ads in previous months",
      },
    },
  },

  customers: {
    heading: "Customers",
    /**
     * Names the product rather than the two ad types. The tab lists both kinds
     * of conversion and its Ad type column already splits them, so the subhead
     * doesn't have to.
     */
    subhead:
      "An overview of the customers you've converted through promoted listings.",
    /**
     * One of the three tab subheads V3 writes as a set — see
     * `overview.v3Subhead` for the shape.
     */
    v3Subhead:
      "See which retailers you've converted through Promoted Listings.",
    customerValueCardTitle: "Ad-acquired new customer metrics",

    kpi: {
      averageOrderCountTitle: "Average order count",
      averageFirstYearOrderCountTitle: "Average first-year order count",
      averageFirstYearValueTitle: "Average first-year value",
      /**
       * The three filter states of the Total conversions card. `all` is `new`
       * and `returning` joined word for word, so a reader moving the filter
       * watches one total split rather than reading three sentences and
       * wondering whether they describe three metrics.
       *
       * That join is why the second half counts reorders rather than the
       * retailers who placed them, and it matches the figure: a retailer who
       * reorders twice in the range is two conversions, not one customer.
       * Naming customers on both sides would read tidier and be wrong.
       *
       * `overview.kpi.conversions` carries `all` word for word on the Overview
       * tab's version of this card. Edit them together.
       */
      conversions: {
        all: "Customers acquired through new customer ads and reorders placed through reorder ads.",
        new: "Customers acquired through new customer ads.",
        returning: "Reorders placed through reorder ads.",
      },
      orders: {
        all: "Every order placed by the customers your ads acquired or brought back, their reorders included.",
        new: "Every order placed by the customers your new customer ads acquired, their reorders included.",
      },
      avgOrders:
        "The average number of orders placed by each customer acquired through new customer ads.",
      avgFirstYearValue:
        "The average amount a retailer orders from this brand in their first year after being acquired through a new customer ad.",
      /**
       * V3 only. The order average is cut to the same first-year window as the
       * value average beside it, so both tooltips name that window in the same
       * voice.
       */
      avgOrderCountFirstYear:
        "Average number of orders that an ad-acquired new customer places from you in their first year.",
      avgFirstYearValueV3:
        "Average amount that an ad-acquired new customer spends on your products in their first year.",
      /**
       * Inputs under the two averages. Both windows are the advertiser's
       * whole tenure, capped at twelve months after each acquisition — the
       * same counts the headline divides. {value} is the formatted figure.
       */
      avgOrderCountOrders: "{value} orders",
      avgOrderCountCustomers: "{value} new customers",
      avgFirstYearValueSales: "{value} from new customer ads",
      avgFirstYearValueCustomers: "{value} new customers",
      avgValue: {
        new: "The average ad sales from each customer acquired through new customer ads.",
        returning:
          "The average ad sales from each reorder placed through reorder ads.",
      },
      avgCost: {
        new: "New customer ad spend divided by the number of customers acquired.",
        returning: "Reorder ad spend divided by the number of reorders placed.",
      },
    },

    table: {
      heading: "Conversions",
      /**
       * Says who is in the list and what order it's in — the two things that
       * decide whether a reader trusts what they're scanning. The sort clause
       * describes the default the header ships with (Order date, descending);
       * it stops being true if that column's sort is ever made interactive.
       *
       * Both halves name a customer, unlike the conversions card above, because
       * the rows are retailers: one line each, however many reorders sit behind
       * it. The second half is in the ad's voice for that reason — a reorder ad
       * brings a retailer back — which is also what keeps it off "reordered
       * through reorder ads". "By" is the agent of the passive here, not the
       * "via" the file bans.
       */
      headingTooltip:
        "Customers acquired through new customer ads or brought back by reorder ads, sorted by most recent ad-attributed order.",
      /**
       * A noun phrase rather than a sentence about the list, so it reads like
       * the other column definitions. Naming both values costs a clause and
       * buys what the cells can't carry: the labels say which ad ran, not what
       * it did, and acquiring a customer and bringing one back are the whole
       * difference between the two rows.
       */
      adType:
        "The ad type that converted a retailer; either a new customer ad that acquired a customer or a reorder ad that brought back an existing one.",
      empty: "No customers match your filters.",
    },

    storeTypeChart: {
      title: "Monthly performance by store type",
      titleTooltip:
        "Data for the current month is excluded until it's fully closed out, so recent activity may lag by up to one month.",
      boostedDisabled: "Available once you're boosting specific store types.",
    },

    storeTypeTable: {
      title: "Cumulative performance by store type",
      titleTooltip:
        "Data for the current month is excluded until it's fully closed out, so recent activity may lag by up to one month.",
    },

    /**
     * The expand toggle the two store-type cards share. Side by side they split
     * the page 5/7, which is tight for seven columns and a legend; expanding
     * hands one of them the full width without asking the reader to resize
     * anything. Verbs rather than states: the label says what the click does.
     */
    storeTypeCards: {
      expand: "Expand to full width",
      collapse: "Collapse",
    },
  },

  products: {
    heading: "Products",
    subhead:
      "How your individual products are performing in Promoted Listings, including impressions, clicks, and click-through rates.",
    /**
     * Third of V3's three tab subheads — see `overview.v3Subhead`. Drops the
     * column list the V1/V2 sentence carries: the table's own headers name
     * impressions, clicks, and click-through rate a row below it.
     */
    v3Subhead:
      "See how each of your products is performing in Promoted Listings.",
  },

  cohort: {
    /**
     * "by month", not "by conversions month" — the swap away from "cohort"
     * doesn't survive as a modifier. Nothing is lost: the page is reached by
     * clicking a month and every figure on it is scoped to that month, so the
     * shorter form says the same thing. v2 names the month in the heading
     * instead; this is the v1 title.
     */
    heading: "Ad conversions by month",
    subhead:
      "The performance of your ad conversions from {month} over the 12 months that follow.",

    kpi: {
      /**
       * `customers.kpi.conversions.all` with the cohort's scope hung off the
       * end. The trailing clause is the only difference and is the entire
       * reason this key isn't the other one; keep the definition ahead of it in
       * step with that key, and keep the clause last so the definition reads
       * the same on every surface before the scope narrows it.
       */
      conversions:
        "Customers acquired through new customer ads and reorders placed through reorder ads, in {month}.",
      adSpend:
        "Total amount spent on ads in {month}. Ad spend is updated weekly and finalized on the 4th of the following month.",
      immediateAdSales:
        "Revenue from all orders placed within 14 days of {month}'s new customer ad clicks or reorder ad clicks.",
      continuedTitle: "First-year reorders",
      /**
       * `overview.continuedAdSales.all` with the month named instead of "each
       * month", so the drilldown card and the Overview column a click apart
       * define the figure the same way. Edit them together.
       */
      continuedAdSales:
        "Revenue from all orders placed by {month}'s new customers within 12 months of their first order.",

      firstYearTitle: "First-year sales",
      /**
       * Qualified only while part of the figure is still projected. Once the
       * cohort has its full 12 months the number is settled, and carrying
       * "(forecasted)" past that point would have a reader discounting a figure
       * that is final.
       */
      firstYearForecastTitle: "First-year sales (forecasted)",
      firstYearRoasTitle: "First-year ROAS",
      /**
       * Same qualifier as `firstYearForecastTitle`: only while the return is
       * still an estimate. A matured cohort's ROAS is settled, so the
       * parentheses come off with the forecast link.
       */
      firstYearRoasForecastTitle: "First-year ROAS (forecasted)",
      firstYearAdSales:
        "Total revenue over 12 months from the customers acquired in {month}. These conversions have passed the 12-month mark, so the figure is final.",
      /**
       * `overview.monthTable.firstYearAdSales` with the month named instead of
       * "each month", so the drilldown card and the Overview column a click
       * apart define the figure the same way. Shared with the two cohort chart
       * legend items carrying this label. Edit them together.
       */
      firstYearAdSalesForecast:
        "Forecasted total revenue over 12 months from the customers acquired in {month}. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
    },

    /**
     * Both name the month once, at the front, and refer back to it after. They
     * used to open on "this month" and close on the month by name, which read
     * as two different months to anyone not already sure they were the same.
     */
    liftTag: {
      realized:
        "The customers you acquired in {month} have brought in **{amount}** in additional orders since then, which is a **{pct}% lift** over Ad sales.",
      forecast:
        "The customers you acquired in {month} are expected to bring in **{amount}** in additional orders within 12 months, which would be a **{pct}% lift** over Ad sales.",
    },

    chart: {
      salesTitle: "First-year sales (actual)",
      ordersTitle: "Total orders",
      salesToDate:
        "Total ad-attributed revenue from {month} conversions through the selected month.",
      /** Same label as the KPI card, so same sentence — see `kpi.firstYearAdSalesForecast`. */
      salesForecast:
        "Forecasted total revenue over 12 months from the customers acquired in {month}. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
      adSpend: "Total amount spent on ads in {month}.",
      ordersToDate:
        "Total ad-attributed orders from {month} conversions through the selected month.",
      ordersForecast:
        "Estimated additional orders from {month} conversions over their first 12 months.",
    },

    table: {
      heading: "Conversions this month",
      /**
       * `overview.monthTable.conversions` scoped to the one month, so the column
       * a reader clicked and the table they landed on define conversions the
       * same way. Shared: v2 reads this too, since its table counts the same
       * thing under a shorter heading. Edit them together.
       */
      headingTooltip:
        "New customer acquisitions and reorder conversions from promoted listings in {month}.",
      /** "Conversions" rather than "customers": a reorder row isn't a new one. */
      exploreAll: "Explore all conversions",
      continuedSales:
        "Revenue from the orders this customer placed after the one that acquired them, within 12 months of that first order.",
      liftTag:
        "This customer has brought in **{amount}** in first-year reorders since their first order, which is a **{pct}% lift** over their Ad sales.",
      /** One state now that the table lists both ad types together. */
      empty: "No ad conversions this month.",
    },

    /**
     * The v2 drilldown. Only the strings that actually differ live here; v2
     * reads everything else from the block above, so a fix to a shared
     * definition can't land in one version and miss the other.
     *
     * The split exists because v2 reframes the page around the first-year
     * total: the headline metrics close on it, the chart plots the three bands
     * that add up to it, and the table drops the intermediate money columns.
     */
    v2: {
      /**
       * The month is the subject now that the page is one month's performance,
       * not a generic conversions-by-month view. Short form (`Jan 2026`)
       * matches the table and chart axes a click away.
       */
      heading: "{month} ad performance",
      /**
       * V3 drops "ad performance" so the page title and the conversions table
       * name the same thing. Capital C matches the KPI card beside the chart.
       */
      v3Heading: "{month} Conversions",
      /**
       * Names both halves of the page in the order they appear: the month's own
       * conversions in the table, and the first year those conversions go on to
       * produce in the metrics and chart above it. Sits under the heading so
       * the divider below it is the page break, not a second intro.
       */
      subhead:
        "Explore the ad conversions from this month, and the first-year performance of customers you acquired through new customer ads.",

      kpi: {
        /**
         * The last of the four headline metrics: the first-year figure beside
         * it over the month's ad spend. Used while the cohort is still under
         * 12 months; the title then carries "(forecasted)" and the card links
         * How we forecast.
         *
         * Word-for-word `overview.monthTable.firstYearRoas` with this cohort's
         * month in place of "each month". The column a reader clicked and the
         * card they land on are the same metric, so they get the same
         * sentence. Edit them together.
         */
        firstYearRoas:
          "The return we estimate you will have in 12 months' time, given the reorders from customers acquired in {month}.",
        /**
         * Once the cohort has its full 12 months. Parallel to
         * `cohort.kpi.firstYearAdSales`: names the same return, then says the
         * figure is final so a reader doesn't look for a forecast that isn't
         * there.
         */
        firstYearRoasFinal:
          "The return over 12 months from the customers acquired in {month}. These conversions have passed the 12-month mark, so the figure is final.",
      },

      chart: {
        /**
         * Names the destination rather than the plotted series: the line climbs
         * toward the first-year total, and that total is the figure the metrics
         * beside it close on. The month is repeated here even though the page
         * heading states it, because the chart is the one element a reader
         * screenshots out of context.
         */
        title: "First-year sales of {month} conversions",
        /**
         * Hung off the title, so it covers the chart as a whole rather than one
         * series: the solid line and the dashed one are the same measure at two
         * points in time, and that relationship is the thing neither legend
         * item can state on its own.
         */
        titleTooltip:
          "Total ad sales from customers acquired in {month} to date. The dashed line represents where we forecast those sales will be at the 12-month mark.",
        /**
         * The realized area, and the only place this page defines total ad
         * sales now that the headline metrics lead with the first-year pair.
         *
         * Word-for-word `overview.monthTable.firstYearSalesActual` with this
         * cohort's month in place of "each month", so the Overview's Total ad
         * sales column and this legend item define one figure. Edit them
         * together.
         */
        salesToDate:
          "Ad sales plus first-year reorders: everything {month}'s ad conversions have brought in so far. Reorders from ad-acquired new customers count only for the first 12 months after their first order.",
        /**
         * The dotted tail and its end marker. Separate from the definition
         * above because a reader hovering the forecast is asking a different
         * question than one hovering the solid line.
         *
         * Same label as the KPI card, so same sentence — see
         * `kpi.firstYearAdSalesForecast`.
         */
        salesForecast:
          "Forecasted total revenue over 12 months from the customers acquired in {month}. The estimate becomes more accurate as customers place additional orders, and after 12 months it's replaced by actual first-year sales.",
        adSpend: "Total amount spent on ads in {month}.",
      },

      table: {
        /**
         * Names the cohort on the table itself, not only in the page heading.
         * Short form (`Jul 2026`) matches the chart axes and the heading a
         * scroll away, so a screenshot of the table still says which month
         * these rows belong to.
         */
        heading: "{month} conversions",
        searchPlaceholder: "Search by store name",
        /**
         * Replaces v1's "Ad sales" column. The table lists one order
         * per row, so naming the row's own amount is plainer than naming the
         * metric that aggregates it — and it stops the column from reading as
         * a total.
         */
        orderAmount:
          "What this retailer spent on the order the ad drove. {month}'s Ad sales is the sum of this column.",
        /** Distinct from v1's: this table filters, so a reader can empty it. */
        empty: "No conversions match your filters.",
      },
    },
  },

  settings: {
    /**
     * The panel that used to be "Target audience".
     *
     * Local to this prototype on purpose. The module it replaces lives in
     * pages/Ads2DControlsAlphaBeta/shared.tsx and is rendered by five other
     * prototypes, which keep the audience framing; only this one reframes the
     * choice as which kind of ad runs.
     *
     * The two option names are the ones the Overview's ad type filter uses, so
     * the setting and the figures it moves are named the same thing in both
     * places.
     */
    adType: {
      heading: "Ad type",
      /**
       * Diverges from the audience version, which asked which retailers to
       * reach. The options are campaign types, so the campaign is what's being
       * chosen and the audience is the basis for choosing — the other way round
       * from how the shared string read.
       */
      subhead:
        "Choose which types of ad campaigns you want to run based on who you want to reach.",
      both: {
        title: "New customer ads and reorder ads",
        description:
          "Acquire new customers and drive reorders from existing customers.",
        tag: "Recommended",
      },
      new: {
        title: "New customer ads only",
        description: "Acquire new customers only.",
      },
    },
    budgetLimitingTag: "Budget is limiting reach",
    productBoosting:
      "Since your budget is tight, Faire is focusing more of it on your new products, which may mean your other products show up less often. A higher budget could help you reach both without tradeoffs.",
    preferredStoreTypes:
      "Since your budget is tight, Faire is focusing more of it on your preferred store types, which may mean your other store types show up less often. A higher budget could help you reach both without tradeoffs.",
  },
} as const;
